import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { Reveal } from "@/components/Reveal";
import { Btn, SectionLabel } from "@/components/ui-bits";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const { data: p, isError } = useQuery({ queryKey: ["portfolio", slug], queryFn: async () => (await api.get(`/portfolio/${slug}`)).data });
  const { data: all = [] } = useQuery({ queryKey: ["portfolio"], queryFn: async () => (await api.get("/portfolio")).data });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });

  if (isError) return <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-40 text-center"><h1 className="font-display font-black text-4xl">Project tidak ditemukan</h1><Link to="/portfolio" className="link-underline overline text-xs mt-4 inline-block">Kembali ke Portfolio</Link></div>;
  if (!p) return <div className="py-40 text-center overline text-xs text-muted-foreground">Memuat…</div>;

  const idx = all.findIndex((x) => x.slug === slug);
  const next = all[(idx + 1) % all.length];
  const relatedServices = services.filter((s) => (p.related_services || []).includes(s.slug));
  const jsonLd = { "@context": "https://schema.org", "@type": "CreativeWork", name: p.title, about: p.industry, creator: { "@type": "Organization", name: "Digitalis" } };

  const Block = ({ label, title, children }) => (
    <section className="border-t border-border py-16"><div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-4"><SectionLabel>{label}</SectionLabel><h2 className="font-display font-black tracking-tightest text-3xl md:text-4xl">{title}</h2></div>
      <div className="lg:col-span-8 text-lg text-muted-foreground leading-relaxed">{children}</div>
    </div></section>
  );

  return (
    <div data-testid="portfolio-detail-page">
      <Seo title={p.seo_title} description={p.seo_description} canonicalPath={`/portfolio/${p.slug}`} image={p.cover_image} jsonLd={jsonLd} />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 md:pt-16 pb-10">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Portfolio", to: "/portfolio" }, { label: p.title }]} />
        <h1 className="font-display font-black tracking-tightest break-words leading-[0.95] text-[2.5rem] sm:text-5xl md:text-7xl mt-6 md:mt-8 max-w-4xl">{p.title}</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 mt-8 md:mt-10 border-t border-border pt-6 md:pt-8">
          {[["Klien", p.client], ["Industri", p.industry], ["Tahun", p.year], ["Layanan", (p.services_used || []).join(", ")]].map(([k, v]) => (
            <div key={k}><p className="mb-1 text-xs font-semibold text-muted-foreground">{k}</p><p className="font-medium">{v || "-"}</p></div>
          ))}
        </div>
      </section>
      <div className="mx-auto max-w-[1400px] px-5 md:px-10"><div className="relative overflow-hidden border border-border aspect-[16/9]"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div></div>

      <Block label="Overview" title="Ringkasan">{p.summary}</Block>
      <Block label="The Challenge" title="Tantangan">{p.challenge}</Block>
      <Block label="Our Approach" title="Strategi">{p.strategy}</Block>
      <Block label="Execution" title="Eksekusi">{p.execution}</Block>
      <Block label="Results" title="Hasil">{p.results}</Block>

      {(p.gallery || []).length > 0 && (
        <section className="border-t border-border py-16"><div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionLabel>Gallery</SectionLabel>
          <div className="grid md:grid-cols-2 gap-6 mt-6">{p.gallery.map((g, i) => <div key={i} className="relative overflow-hidden border border-border aspect-[4/3] group"><img src={g} alt={`${p.title} ${i + 1}`} className="w-full h-full object-cover img-grayscale" loading="lazy" /></div>)}</div>
        </div></section>
      )}

      {relatedServices.length > 0 && (
        <section className="border-t border-border py-16"><div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionLabel>Related Services</SectionLabel>
          <div className="grid md:grid-cols-3 gap-px bg-border border border-border mt-6">{relatedServices.map((s) => <Link key={s.id} to={`/services/${s.slug}`} className="group bg-background p-8 hover:bg-secondary transition-colors"><h3 className="font-display font-bold text-2xl mb-2">{s.name}</h3><p className="text-sm text-muted-foreground">{s.short_description}</p></Link>)}</div>
        </div></section>
      )}

      {next && (
        <Link to={`/portfolio/${next.slug}`} className="group block border-t border-border py-16 hover:bg-secondary/40 transition-colors"><div className="mx-auto max-w-[1400px] px-5 md:px-10"><SectionLabel>Project Berikutnya</SectionLabel><h2 className="break-words font-display text-4xl font-black tracking-tightest group-hover:text-primary md:text-6xl">{next.title}</h2></div></Link>
      )}

      <section className="border-t border-border py-20 text-center md:py-24"><div className="mx-auto max-w-[1400px] px-5 md:px-10"><h2 className="mb-8 font-display text-3xl font-black tracking-tightest sm:text-4xl md:text-6xl">Ingin Hasil Serupa untuk Brand Anda?</h2><Btn to="/contact">Mulai Konsultasi</Btn></div></section>
    </div>
  );
}
